import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ChatConversationController } from 'src/@core/APIs/ChatConversationController';
import { environment } from '../../../environments/environment';
import { ChatRequestPayload, ChatResponse } from '../models/chat-session-api.model';
import { shouldRetryOnFallback } from '../utils/chat-session-api.utils';

@Injectable({
  providedIn: 'root'
})
export class ChatSessionTransportService {
  private readonly chatApiUrl = (environment.chatApiUrl || '').trim()
    || (environment.apiBaseUrl ? `${environment.apiBaseUrl}/api/ChatConversation/SendMessage` : '/api/ChatConversation/SendMessage');

  constructor(private readonly httpClient: HttpClient) {}

  postChatMessage(
    payload: ChatRequestPayload,
    headers: HttpHeaders | undefined,
    observer: {
      next: (response: ChatResponse) => void;
      error: (err: unknown) => void;
    },
  ): void {
    this.tryPostWithFallback(this.resolveChatEndpoints(), payload, headers, observer);
  }

  getConversations(
    headers: HttpHeaders | undefined,
    observer: {
      next: (response: unknown) => void;
      error: (err: unknown) => void;
    },
  ): void {
    this.tryGetWithFallback(this.resolveConversationListEndpoints(), headers, observer);
  }

  getConversationDetail(
    conversationId: string,
    headers: HttpHeaders | undefined,
    observer: {
      next: (response: unknown) => void;
      error: (err: unknown) => void;
    },
  ): void {
    this.tryGetWithFallback(this.resolveConversationDetailEndpoints(conversationId), headers, observer);
  }

  getUsageBalance(
    headers: HttpHeaders | undefined,
    observer: {
      next: (response: unknown) => void;
      error: (err: unknown) => void;
    },
  ): void {
    this.tryGetWithFallback(this.resolveUsageBalanceEndpoints(), headers, observer);
  }

  getPresetQuestions(
    headers: HttpHeaders | undefined,
    observer: {
      next: (response: unknown) => void;
      error: (err: unknown) => void;
    },
  ): void {
    this.tryGetWithFallback(this.resolvePresetQuestionEndpoints(), headers, observer);
  }

  deleteConversation(
    conversationId: string,
    headers: HttpHeaders | undefined,
    observer: {
      next: (response: unknown) => void;
      error: (err: unknown) => void;
    },
  ): void {
    this.tryDeleteWithFallback(this.resolveDeleteConversationEndpoints(conversationId), headers, observer);
  }

  private resolveChatEndpoints(): string[] {
    const urls = [
      this.chatApiUrl,
      environment.apiBaseUrl ? `${environment.apiBaseUrl}/api/ChatConversation/SendMessage` : '',
      '/api/ChatConversation/SendMessage',
      environment.apiBaseUrl ? `${environment.apiBaseUrl}/api/chat/ask` : '',
      '/api/chat/ask',
    ].map((x) => (x || '').trim()).filter((x) => !!x);

    return Array.from(new Set(urls));
  }

  private resolveConversationListEndpoints(): string[] {
    const urls = [
      environment.apiBaseUrl ? `${environment.apiBaseUrl}/api/ChatConversation/GetMyConversations` : '',
      '/api/ChatConversation/GetMyConversations',
    ].map((x) => (x || '').trim()).filter((x) => !!x);

    return Array.from(new Set(urls));
  }

  private resolveConversationDetailEndpoints(conversationId: string): string[] {
    const encodedConversationId = encodeURIComponent(conversationId);
    const urls = [
      environment.apiBaseUrl ? `${environment.apiBaseUrl}/api/ChatConversation/GetConversation?conversationId=${encodedConversationId}` : '',
      `/api/ChatConversation/GetConversation?conversationId=${encodedConversationId}`,
    ].map((x) => (x || '').trim()).filter((x) => !!x);

    return Array.from(new Set(urls));
  }

  private resolveDeleteConversationEndpoints(conversationId: string): string[] {
    const encodedConversationId = encodeURIComponent(conversationId);
    const urls = [
      environment.apiBaseUrl ? `${environment.apiBaseUrl}/api/ChatConversation/DeleteConversation?conversationId=${encodedConversationId}` : '',
      `/api/ChatConversation/DeleteConversation?conversationId=${encodedConversationId}`,
    ].map((x) => (x || '').trim()).filter((x) => !!x);

    return Array.from(new Set(urls));
  }

  private resolveUsageBalanceEndpoints(): string[] {
    const urls = [
      environment.apiBaseUrl ? `${environment.apiBaseUrl}/api/ChatConversation/GetUsageBalance` : '',
      '/api/ChatConversation/GetUsageBalance',
    ].map((x) => (x || '').trim()).filter((x) => !!x);

    return Array.from(new Set(urls));
  }

  private resolvePresetQuestionEndpoints(): string[] {
    const urls = [
      ChatConversationController.GetPresetQuestions,
      '/api/ChatConversation/GetPresetQuestions',
    ].map((x) => (x || '').trim()).filter((x) => !!x);

    return Array.from(new Set(urls));
  }

  private tryPostWithFallback(
    endpoints: string[],
    payload: ChatRequestPayload,
    headers: HttpHeaders | undefined,
    observer: {
      next: (response: ChatResponse) => void;
      error: (err: unknown) => void;
    },
    index = 0,
    lastError: unknown = null,
  ): void {
    if (index >= endpoints.length) {
      observer.error(lastError);
      return;
    }

    const endpoint = endpoints[index];
    this.httpClient.post<ChatResponse>(endpoint, payload, { headers }).subscribe({
      next: (response) => observer.next(response),
      error: (err) => {
        if (shouldRetryOnFallback(err)) {
          this.tryPostWithFallback(endpoints, payload, headers, observer, index + 1, err);
          return;
        }
        observer.error(err);
      },
    });
  }

  private tryGetWithFallback(
    endpoints: string[],
    headers: HttpHeaders | undefined,
    observer: {
      next: (response: unknown) => void;
      error: (err: unknown) => void;
    },
    index = 0,
    lastError: unknown = null,
  ): void {
    if (index >= endpoints.length) {
      observer.error(lastError);
      return;
    }

    const endpoint = endpoints[index];
    this.httpClient.get<unknown>(endpoint, { headers }).subscribe({
      next: (response) => observer.next(response),
      error: (err) => {
        if (shouldRetryOnFallback(err)) {
          this.tryGetWithFallback(endpoints, headers, observer, index + 1, err);
          return;
        }
        observer.error(err);
      },
    });
  }

  private tryDeleteWithFallback(
    endpoints: string[],
    headers: HttpHeaders | undefined,
    observer: {
      next: (response: unknown) => void;
      error: (err: unknown) => void;
    },
    index = 0,
    lastError: unknown = null,
  ): void {
    if (index >= endpoints.length) {
      observer.error(lastError);
      return;
    }

    const endpoint = endpoints[index];
    this.httpClient.delete<unknown>(endpoint, { headers }).subscribe({
      next: (response) => observer.next(response),
      error: (err) => {
        if (shouldRetryOnFallback(err)) {
          this.tryDeleteWithFallback(endpoints, headers, observer, index + 1, err);
          return;
        }
        observer.error(err);
      },
    });
  }
}
